import React, { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import KpiCard from "../commercial-dashboard/KpiCard";
import { KpiGrid, Section } from "../commercial-dashboard/DashboardBits";
import {
  CHANNEL_COLORS,
  CHANNELS,
  CM_CHANNELS,
  PM_CHANNELS,
  aggregate,
  bridge,
  byPerson,
  eur,
  groupBy,
  inPeriod,
  pct,
  pp,
  sum,
  toneOfCompliance,
  toneOfMarginDiff,
  keur,
  MONTHS,
} from "./controlBoardModel";
import {
  ChannelSwatch,
  Donut,
  Note,
  Num,
  PeriodSelector,
  Signal,
} from "./ControlBoardBits";
import { TabProps, companyIdsOf } from "./boardData";
import { ControlBoardChannel, ControlBoardRow } from "../../types/controlBoard";

/** Resumen del año: grandes cifras, inversión por formato y una tarjeta por account. */
const SummaryTab: React.FC<TabProps> = ({ data, onOpenAccount }) => {
  const theme = useTheme();
  const [month, setMonth] = useState(0);
  const rows = useMemo(
    () => inPeriod(data.rows, month, data.lastMonth),
    [data, month]
  );
  const plan = useMemo(
    () => inPeriod(data.plan, month, data.lastMonth),
    [data, month]
  );
  const totals = aggregate(rows);
  const clients = new Set(rows.map((r) => r.clientId)).size;
  const period = month
    ? MONTHS[month].toLowerCase()
    : `enero a ${MONTHS[data.lastMonth].toLowerCase()}`;

  const byChannel = groupBy(rows, (r) => r.channel);
  const channelRow = (channel: ControlBoardChannel) =>
    aggregate(byChannel.get(channel) ?? []);
  const investment = totals.plannedSale || 1;

  const accounts = useMemo(() => {
    const map = byPerson(rows, (r) => r.accounts);
    const total = sum(rows, (r) => r.plannedSale) || 1;
    return [...map.entries()]
      .map(([name, list]) => {
        const t = aggregate(list);
        const cm = aggregate(list.filter((r) => r.type === "CM"));
        const b = bridge(
          list,
          inPeriod(data.planFor(companyIdsOf(list)), month, data.lastMonth)
        );
        const byClient = [...groupBy(list, (r) => r.client).entries()]
          .map(([client, l]) => ({ client, sale: sum(l, (r) => r.sale) }))
          .sort((a, b2) => b2.sale - a.sale);
        return {
          name,
          t,
          cm,
          b,
          share: t.plannedSale / total,
          clients: byClient.length,
          top: byClient[0],
        };
      })
      .sort((a, b) => b.t.plannedSale - a.t.plannedSale);
  }, [rows, data, month]);

  const group = (
    label: string,
    channels: ControlBoardChannel[],
    list: ControlBoardRow[]
  ) => {
    const t = aggregate(list);
    const isPM = channels === PM_CHANNELS;
    return (
      <React.Fragment key={label}>
        <TableRow
          sx={{
            "& td": {
              fontWeight: 600,
              backgroundColor: theme.palette.grey[50],
            },
          }}
        >
          <TableCell>{label}</TableCell>
          <Num>{eur(t.plannedSale)}</Num>
          <Num>{pct(t.plannedSale / investment, 1)}</Num>
          <Num>{eur(t.sale)}</Num>
          <Num>{isPM ? "—" : eur(t.profit)}</Num>
          <Num>{isPM ? "—" : pct(t.margin, 1)}</Num>
        </TableRow>
        {channels
          .filter(
            (c) => channelRow(c).plannedSale > 0 || channelRow(c).sale > 0
          )
          .map((c) => {
            const r = channelRow(c);
            return (
              <TableRow key={c} hover>
                <TableCell sx={{ pl: 4 }}>
                  <ChannelSwatch channel={c} />
                  {c}
                </TableCell>
                <Num>{eur(r.plannedSale)}</Num>
                <Num muted>{pct(r.plannedSale / investment, 1)}</Num>
                <Num>{eur(r.sale)}</Num>
                <Num>{isPM ? "—" : eur(r.profit)}</Num>
                <Num>{isPM ? "—" : pct(r.margin, 1)}</Num>
              </TableRow>
            );
          })}
      </React.Fragment>
    );
  };

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Inversión gestionada = la de los briefings. Facturación = la venta
          real. Periodo: {period} {data.year}.
        </Typography>
        <PeriodSelector
          value={month}
          lastMonth={data.lastMonth}
          onChange={setMonth}
        />
      </Stack>

      <KpiGrid columns={5}>
        <KpiCard
          label="Inversión gestionada"
          value={eur(totals.plannedSale)}
          caption={`${clients} clientes · ${totals.lines.toLocaleString("es-ES")} líneas`}
        />
        <KpiCard
          label="Facturación"
          value={eur(totals.sale)}
          caption={`${pct(totals.compliance, 0)} de la inversión gestionada`}
        />
        <KpiCard
          label="Beneficio bruto"
          value={eur(totals.profit)}
          caption="Facturación − coste de plataforma"
        />
        <KpiCard
          label="Margen bruto"
          value={pct(totals.margin)}
          caption={`Sobre lo que lleva margen (${keur(totals.saleWithMargin)}); el paid media que paga el cliente no cuenta`}
        />
        <KpiCard
          label="Fee"
          value={eur(totals.fee)}
          caption={`Fee de los briefings sobre la facturación · plan ${eur(sum(plan, (l) => l.fee))}`}
        />
      </KpiGrid>

      <Section
        title="Inversión por formato"
        subtitle="Inversión de cada formato y su peso sobre el total. El margen no cuenta la facturación de Paid Media."
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          gap={4}
          alignItems="center"
        >
          <Donut
            size={220}
            parts={CHANNELS.map((c) => ({
              label: c,
              value: channelRow(c).plannedSale,
              color: CHANNEL_COLORS[c],
            }))}
            centerValue={keur(totals.plannedSale)}
            centerLabel="inversión"
          />
          <Box sx={{ flex: 1, minWidth: 0, overflowX: "auto", width: "100%" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Formato</TableCell>
                  <Num>Inversión</Num>
                  <Num>Peso</Num>
                  <Num>Facturación</Num>
                  <Num>Beneficio bruto</Num>
                  <Num>Margen</Num>
                </TableRow>
              </TableHead>
              <TableBody>
                {group(
                  "Compra de medios",
                  CM_CHANNELS,
                  rows.filter((r) => r.type === "CM")
                )}
                {group(
                  "Paid Media",
                  PM_CHANNELS,
                  rows.filter((r) => r.type === "PM")
                )}
                <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                  <TableCell>Total</TableCell>
                  <Num>{eur(totals.plannedSale)}</Num>
                  <Num>100 %</Num>
                  <Num>{eur(totals.sale)}</Num>
                  <Num>{eur(totals.profit)}</Num>
                  <Num>{pct(totals.margin, 1)}</Num>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </Section>

      <Section
        title="Por account"
        subtitle="Inversión de su cartera, reparto entre compra de medios y Paid Media y beneficio que deja. Pulsa una tarjeta para ver sus clientes."
      >
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          }}
        >
          {accounts.map((a) => {
            const compliance = a.b.planInvestment
              ? a.b.realInvestment / a.b.planInvestment
              : NaN;
            const shift = a.b.realShareCM - a.b.planShareCM;
            return (
              <ButtonBase
                key={a.name}
                onClick={() => onOpenAccount(a.name)}
                sx={{ textAlign: "left", borderRadius: 3, display: "block" }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    height: "100%",
                    "&:hover": { borderColor: theme.palette.grey[400] },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {a.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ver clientes ›
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {a.clients} {a.clients === 1 ? "cliente" : "clientes"} ·{" "}
                    {pct(a.share, 0)} de la inversión total
                  </Typography>
                  <Box
                    component="dl"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: "6px 12px",
                      m: 0,
                      mt: 1.5,
                      "& dd": { m: 0, textAlign: "right", fontWeight: 600 },
                      "& dt": { color: "text.secondary" },
                    }}
                  >
                    <dt>Inversión gestionada</dt>
                    <dd>{eur(a.t.plannedSale)}</dd>
                    <dt>Facturación</dt>
                    <dd>{eur(a.t.sale)}</dd>
                    <dt>· Compra de medios</dt>
                    <dd>
                      {eur(a.t.saleCM)}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {pct(a.t.sale ? a.t.saleCM / a.t.sale : NaN, 0)}
                      </Typography>
                    </dd>
                    <dt>· Paid Media</dt>
                    <dd>{eur(a.t.salePM)}</dd>
                    <dt>Beneficio bruto</dt>
                    <dd>{eur(a.t.profit)}</dd>
                    <dt>Margen compra de medios</dt>
                    <dd>{a.cm.sale ? pct(a.cm.margin, 1) : "—"}</dd>
                    <dt>Cliente principal</dt>
                    <dd>{a.top?.client ?? "—"}</dd>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack
                    direction="row"
                    gap={1.5}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Del Business Plan
                      </Typography>
                      {a.b.planInvestment ? (
                        <Signal
                          text={pct(compliance, 0)}
                          tone={toneOfCompliance(compliance)}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sin plan
                        </Typography>
                      )}
                    </Box>
                    <Box textAlign="right">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Compra de medios vs BP
                      </Typography>
                      {Number.isFinite(shift) ? (
                        <Signal
                          text={pp(shift, 1)}
                          tone={toneOfMarginDiff(shift)}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              </ButtonBase>
            );
          })}
        </Box>
        <Note>
          Account y técnico son los responsables que tenía la empresa en cada
          mes. Las líneas de una empresa sin account aparecen en «Sin asignar».
        </Note>
      </Section>
    </Stack>
  );
};

export default SummaryTab;
